import { useEffect, useMemo, useState } from "react";
import { useTran } from "@/hooks";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { frameworkInfos } from "@/lib/constants/frameworks";
import { PATHS } from "@/lib/router";
import { Can } from "@/hooks/useCan/Can";
import { useAuthStore } from "@/features/auth/hooks";
import { cn } from "@/lib/utils";

const EsgForm = () => {
  const { t } = useTran();
  const navigate = useNavigate();
  const [active, setActive] = useState("SGX");
  const { user, isJJSEA } = useAuthStore();
  const permissions = user?.permissions || [];

  useEffect(() => {
    if (frameworkInfos.length > 0) {
      setActive(frameworkInfos[0].key);
    }
  }, []);

  const activeData = useMemo(
    () =>
      frameworkInfos.find((item) => item.key === active) || frameworkInfos[0],
    [active],
  );

  const activePoints = useMemo(() => activeData?.points || [], [activeData]);

  const canCreate = useMemo(() => !activeData?.disabled, [activeData]);

  const handleCreateForFramework = (framework) => {
    if (!framework || framework.disabled) return;
    const path = PATHS.ESG_REPORT.replace(":framework", framework.key).replace(
      ":stepId",
      1,
    );
    navigate(path);
  };

  const handleCreate = () => handleCreateForFramework(activeData);

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("ESG - Reporting Frameworks")}
        description={t("Review frameworks and choose the one to start with.")}
      />
      <div className="flex flex-col gap-6">
        {/* Top Grid of Framework Cards */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {frameworkInfos
            .filter((f) => {
              if (isJJSEA) return true;
              return f.key !== "JJ-Group";
            })
            .map((framework) => {
              const isActive = framework.key === active;
              const isPendingNoData =
                Boolean(framework?.disabled) &&
                !framework?.summary &&
                !framework?.usage &&
                !(framework?.points?.length > 0);
              return (
                <button
                  key={framework.key}
                  type="button"
                  onClick={() => setActive(framework.key)}
                  onDoubleClick={() => handleCreateForFramework(framework)}
                  className={cn(
                    "group relative flex w-full flex-col items-center justify-center gap-3 rounded-radius border px-3 py-4 text-center shadow-sm transition hover:-translate-y-[1px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    isActive
                      ? cn(
                          "border-primary/70 ring-1 ring-primary/30",
                          isPendingNoData ? "bg-muted" : "bg-primary/10",
                        )
                      : isPendingNoData
                        ? "border-border bg-muted"
                        : "border-border bg-background/40 hover:border-primary/40 hover:bg-muted/20",
                  )}
                >
                  {/* Logo Container */}
                  <div className="flex h-12 w-12 items-center justify-center">
                    {framework.image ? (
                      <img
                        src={framework.image}
                        alt={`${framework.value} logo`}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <div className="h-8 w-8 animate-pulse rounded-radius bg-muted/60" />
                    )}
                  </div>

                  {/* Titles */}
                  <div className="space-y-1">
                    <h3
                      className={cn(
                        "text-sm font-bold uppercase tracking-wider",
                        isActive ? "text-primary" : "text-foreground",
                      )}
                    >
                      {framework.key}
                    </h3>
                    <p className="line-clamp-1 text-[10px] text-muted-foreground">
                      {framework.value}
                    </p>
                  </div>
                </button>
              );
            })}
        </div>

        {/* Detailed Info Section Below */}
        <section className="rounded-2xl border border-border bg-muted/20 px-6 py-3">
          <div className="flex flex-col items-start justify-between gap-8 md:flex-row">
            <div className="flex flex-1 gap-6">
              {/* Header with Icon */}
              {activeData?.image && (
                <div className="shrink-0">
                  <img
                    src={activeData.image}
                    alt="icon"
                    className="size-24 object-contain"
                  />
                </div>
              )}

              {/* Description and Lists */}
              <div className="max-w-3xl space-y-4">
                <h3 className="text-xl font-semibold text-foreground">
                  {activeData?.value} Report
                </h3>
                <p className="text-sm text-muted-foreground">
                  {activeData?.summary}
                </p>
                <p className="text-sm italic text-muted-foreground">
                  {activeData?.usage}
                </p>

                {activePoints.length > 0 && (
                  <ul className="space-y-3 text-sm text-muted-foreground">
                    {activePoints.map((point, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/60" />
                        {point}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Action Button - Pushed to the end */}
            <div className="shrink-0 self-center md:self-start">
              {canCreate ? (
                <Can
                  resource="reports"
                  action="create"
                  userPermissions={permissions}
                >
                  <Button onClick={handleCreate} size="lg">
                    {t("Create Report")}
                  </Button>
                </Can>
              ) : (
                <Button size="lg" variant="outline" disabled>
                  {t("Pending")}
                </Button>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default EsgForm;
